import type { NavItem } from "./types";

/** True when `pathname` is `prefix` or below it (segment-wise). */
export function isPathWithin(pathname: string, prefix: string) {
  if (prefix === "/") return true;
  const clean = prefix.replace(/\/+$/, "");
  return pathname === clean || pathname.startsWith(`${clean}/`);
}

function itemMatches(item: NavItem, pathname: string) {
  if (item.exact) return pathname === item.href.replace(/\/+$/, "");
  if (item.activePrefix && isPathWithin(pathname, item.activePrefix)) {
    return true;
  }
  return isPathWithin(pathname, item.href);
}

/**
 * The chain of items (outermost first) that is active for `pathname`.
 * Children win over their parents; among siblings the longest match wins.
 */
export function activeItemChain(items: NavItem[], pathname: string): NavItem[] {
  let best: NavItem[] = [];
  let bestScore = -1;

  const visit = (list: NavItem[], chain: NavItem[]) => {
    for (const item of list) {
      const next = [...chain, item];
      if (item.children?.length) visit(item.children, next);
      if (!itemMatches(item, pathname)) continue;
      // Deeper chains and longer hrefs are more specific.
      const score = next.length * 10_000 + item.href.length;
      if (score > bestScore) {
        best = next;
        bestScore = score;
      }
    }
  };
  visit(items, []);
  return best;
}

/** Flattened items, depth-first. */
export function flattenItems(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => [item, ...flattenItems(item.children ?? [])]);
}

/** Pathname plus search, without the side panel parameter. */
export function stripPanelParam(pathname: string, search: string) {
  const params = new URLSearchParams(search);
  params.delete(PANEL_PARAM);
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

export const PANEL_PARAM = "panel";

/** Builds a main-view URL with the panel parameter set (or removed). */
export function withPanelParam(
  pathname: string,
  search: string,
  hash: string,
  panel: string | null,
) {
  const params = new URLSearchParams(search);
  if (panel) params.set(PANEL_PARAM, panel);
  else params.delete(PANEL_PARAM);
  const query = params.toString();
  return `${pathname}${query ? `?${query}` : ""}${hash}`;
}

/** Absolute href from a router `to` (string or partial path). */
export function hrefFromTo(
  to: string | { pathname?: string; search?: string; hash?: string },
) {
  if (typeof to === "string") return to;
  return `${to.pathname ?? ""}${to.search ?? ""}${to.hash ?? ""}`;
}

/** Splits an absolute href into its path parts. */
export function splitHref(href: string) {
  const url = new URL(href, "http://x");
  return { pathname: url.pathname, search: url.search, hash: url.hash };
}
